// src/pages/Login.jsx
import { useState, useEffect } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import AuthShell from "../components/AuthShell";
import whitelogo from "../assets/whiteintel-white-logo-new.png";
import { getSafeNext } from "../utils/auth";

export default function Login() {
  // API endpoints
  const LOGIN_URL = "https://whiteintel.io/api/regular/app/login_handler_v5.php";
  const MFA_VERIFY_URL = "https://whiteintel.io/api/regular/app/twofactor_handler.php";
  const MFA_RESEND_URL = "https://whiteintel.io/api/regular/app/twofactor_resend_handler.php";
  const AZURE_CODE_EXCHANGE_URL = "https://whiteintel.io/api/regular/app/azure_callback_sso.php";
  const AZURE_REDIRECT_URI = "https://platform.whiteintel.io/login";
  const ACTIVATE_URL = "https://whiteintel.io/api/regular/pub/activate.php";

  // UI state
  const [step, setStep] = useState("login"); // "login" | "sso" | "mfa"
  const [email, setEmail] = useState("");
  const [ssoEmail, setSsoEmail] = useState(""); // dedicated input on the SSO form
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");

  // MFA state
  const [challengeId, setChallengeId] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [useBackup] = useState(false);

  // Resend state
  const [resendLoading, setResendLoading] = useState(false);
  const [resendLeft, setResendLeft] = useState(0); // seconds

  // Cooldown timer
  useEffect(() => {
    if (resendLeft <= 0) return;
    const t = setInterval(() => setResendLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendLeft]);

  /* ---------- 1) Handle Azure OAuth callback (code/state) ---------- */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const oauthError = params.get("error");
    const state = params.get("state");

    if (oauthError) {
      setErr(`Microsoft sign-in failed: ${oauthError}`);
      const url = new URL(window.location.href);
      url.searchParams.delete("code");
      url.searchParams.delete("state");
      url.searchParams.delete("error");
      window.history.replaceState({}, "", url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : ""));
      return;
    }

    if (!code) return;

    (async () => {
      try {
        setLoading(true);
        setErr("");

        const res = await fetch(AZURE_CODE_EXCHANGE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, state, redirect_uri: AZURE_REDIRECT_URI }),
        });

        if (!res.ok) {
          let msg = `HTTP ${res.status}`;
          try {
            const j = await res.json();
            if (j?.error) msg = j.error;
          } catch {}
          throw new Error(msg);
        }

        const json = await res.json();

        if (json?.status === "success" && json?.token) {
          localStorage.setItem("token", json.token);
          // scrub params then redirect home
          const url = new URL(window.location.href);
          url.searchParams.delete("code");
          url.searchParams.delete("state");
          window.history.replaceState({}, "", url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : ""));
          window.location.href = getSafeNext("/");
          return;
        }

        if (json?.twofa === "required" && json?.temp_token) {
          setChallengeId(json.temp_token);
          setStep("mfa");
          const url = new URL(window.location.href);
          url.searchParams.delete("code");
          url.searchParams.delete("state");
          window.history.replaceState({}, "", url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : ""));
          return;
        }

        throw new Error(json?.error || "Microsoft sign-in verification failed");
      } catch (e) {
        setErr(e?.message || "Microsoft sign-in verification failed");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ---------- 2) Handle activation link (?activateToken=...) ---------- */
  useEffect(() => {
    const url = new URL(window.location.href);
    const activateToken = url.searchParams.get("activateToken");
    if (!activateToken) return;

    (async () => {
      try {
        setLoading(true);
        setErr("");
        const res = await fetch(ACTIVATE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: activateToken }),
        });

        const json = await res.json().catch(() => ({}));
        if (!res.ok || json?.success === false) {
          throw new Error(json?.error || `Activation failed (HTTP ${res.status})`);
        }

        setNotice(json?.message || "Your account has been activated. You can now sign in.");
      } catch (e) {
        setErr(e?.message || "Activation failed");
      } finally {
        url.searchParams.delete("activateToken");
        const qs = url.searchParams.toString();
        window.history.replaceState({}, "", url.pathname + (qs ? `?${qs}` : ""));
        setLoading(false);
      }
    })();
  }, []);

  /* ---------- Login / MFA handlers ---------- */
  const onLogin = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);

    try {
      const res = await fetch(LOGIN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: String(email).trim(), password }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || res.statusText || "Authentication failed");

      // SSO redirect case
      if (json?.sso === true && json?.authorize_url) {
        window.location.href = json.authorize_url;
        return;
      }

      // Fully authenticated
      if (json?.status === "success" && json?.token) {
        localStorage.setItem("token", json.token);
        window.location.href = getSafeNext("/");
        return;
      }

      // 2FA required
      if (json?.twofa === "required" && json?.temp_token) {
        setChallengeId(json.temp_token);
        setStep("mfa");
        return;
      }

      throw new Error(json?.error || "Authentication failed");
    } catch (e) {
      setErr(e.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  // Email-only Microsoft SSO entry. Hits the same backend endpoint as the
  // password form — when no password is sent the server replies with an
  // `authorize_url` for Microsoft, which we follow.
  const onSsoLogin = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);

    try {
      const res = await fetch(LOGIN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: String(ssoEmail).trim(), password: "" }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || res.statusText || "SSO sign-in failed");

      if (json?.sso === true && json?.authorize_url) {
        window.location.href = json.authorize_url;
        return;
      }

      // Backend says this email isn't an SSO user — guide them back.
      throw new Error(
        json?.error ||
          "This account isn't configured for Microsoft SSO. Use email + password instead."
      );
    } catch (e) {
      setErr(e.message || "SSO sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  const onVerifyMfa = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const res = await fetch(MFA_VERIFY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          temp_token: challengeId,
          twofactor: mfaCode.trim(),
        }),
      });

      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.error) msg = j.error;
        } catch {}
        throw new Error(msg);
      }

      const json = await res.json();

      if (json?.status === "success" && json?.token) {
        localStorage.setItem("token", json.token);
        window.location.href = getSafeNext("/");
        return;
      }
      throw new Error(json?.error || "MFA verification failed");
    } catch (e) {
      setErr(e.message || "MFA verification failed");
    } finally {
      setLoading(false);
    }
  };

  const onResendMfa = async () => {
    if (!challengeId) {
      setErr("Missing MFA challenge. Please go back and sign in again.");
      return;
    }
    if (resendLeft > 0 || resendLoading) return;

    setErr("");
    setNotice("");
    setResendLoading(true);
    try {
      const res = await fetch(MFA_RESEND_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ temp_token: challengeId }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw new Error(json?.error || `Resend failed (HTTP ${res.status})`);
      }

      setNotice(json?.message || "A new verification code has been sent.");
      setResendLeft(30); // cooldown (seconds)
    } catch (e) {
      setErr(e?.message || "Could not resend code");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <AuthShell>
        {/* Card */}
        <div className="w-full max-w-[420px] rounded-2xl border border-indigo-500/15 bg-gradient-to-b from-slate-900/85 to-[#080d1c]/95 backdrop-blur-xl shadow-[0_0_70px_-10px_rgba(99,102,241,0.22),0_28px_70px_-26px_rgba(0,0,0,0.9)]">
          <div className="px-7 pt-7 pb-6">
            <div className="flex items-center gap-3">
              <img src={whitelogo} alt="WhiteIntel" className="h-8 w-auto shrink-0" />
              <h1 className="text-[19px] font-semibold text-white leading-snug">
                {step === "mfa"
                  ? "Verify your identity"
                  : step === "sso"
                  ? "Continue with Microsoft"
                  : "Welcome back"}
              </h1>
            </div>
            {(step === "mfa" || step === "sso") && (
              <p className="mt-3 text-[13px] text-slate-500 leading-relaxed">
                {step === "mfa"
                  ? "Enter the 6-digit code we just emailed you."
                  : "Enter your work email — we'll redirect you to Microsoft to finish signing in."}
              </p>
            )}

            {err && (
              <div
                className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-[13px] text-red-300"
                role="alert"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="pt-0.5">{err}</span>
                  <button
                    type="button"
                    onClick={() => setErr("")}
                    className="shrink-0 p-1 rounded hover:bg-red-500/20 text-red-300"
                    aria-label="Dismiss error"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {notice && (
              <div className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-[13px] text-emerald-300">
                <div className="flex items-start justify-between gap-3">
                  <span className="pt-0.5">{notice}</span>
                  <button
                    type="button"
                    onClick={() => setNotice("")}
                    className="shrink-0 p-1 rounded hover:bg-emerald-500/20 text-emerald-300"
                    aria-label="Dismiss notice"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {step === "login" && (
              <form onSubmit={onLogin} className="mt-6 space-y-4" noValidate>
                <div>
                  <label className="mb-1.5 block text-[12px] uppercase tracking-wider text-slate-400" htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full rounded-lg border border-slate-700/50 bg-[#05091a] px-3 py-2.5 text-[14px] text-slate-100 placeholder:text-slate-600 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                    autoComplete="username"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[12px] uppercase tracking-wider text-slate-400" htmlFor="password">Password</label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full rounded-lg border border-slate-700/50 bg-[#05091a] pl-3 pr-9 py-2.5 text-[14px] text-slate-100 placeholder:text-slate-600 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-300"
                      aria-label={showPw ? "Hide password" : "Show password"}
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-1">
                  <a href="/recover" className="text-[12px] text-indigo-300 hover:text-indigo-200">Forgot password?</a>
                  <a href="/signup" className="text-[12px] text-indigo-300 hover:text-indigo-200">Register for free</a>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="mt-2 w-full rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2.5 text-[14px] font-semibold text-white hover:from-blue-500 hover:to-indigo-500 shadow-lg shadow-indigo-900/40 disabled:opacity-50 transition-all"
                >
                  {loading ? "Signing in…" : "Sign in"}
                </button>

                {/* Microsoft SSO — opens a dedicated email-only form so users
                    don't have to learn the "leave password empty" trick. */}
                <button
                  type="button"
                  onClick={() => {
                    setErr("");
                    setSsoEmail(email);
                    setStep("sso");
                  }}
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-2.5 text-[14px] font-medium text-slate-200 hover:bg-white/[0.06] hover:border-white/20 disabled:opacity-50 transition-colors"
                >
                  <svg className="h-4 w-4" viewBox="0 0 23 23" aria-hidden="true">
                    <rect width="10" height="10" x="1" y="1" fill="#F25022" />
                    <rect width="10" height="10" x="12" y="1" fill="#7FBA00" />
                    <rect width="10" height="10" x="1" y="12" fill="#00A4EF" />
                    <rect width="10" height="10" x="12" y="12" fill="#FFB900" />
                  </svg>
                  Continue with Microsoft SSO
                </button>
              </form>
            )}

            {step === "sso" && (
              <form onSubmit={onSsoLogin} className="mt-6 space-y-4" noValidate>
                <div>
                  <label className="mb-1.5 block text-[12px] uppercase tracking-wider text-slate-400" htmlFor="sso-email">Email</label>
                  <input
                    id="sso-email"
                    type="email"
                    value={ssoEmail}
                    onChange={(e) => setSsoEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full rounded-lg border border-slate-700/50 bg-[#05091a] px-3 py-2.5 text-[14px] text-slate-100 placeholder:text-slate-600 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                    autoComplete="username"
                    autoFocus
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || !ssoEmail.trim()}
                  className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2.5 text-[14px] font-semibold text-white hover:from-blue-500 hover:to-indigo-500 shadow-lg shadow-indigo-900/40 disabled:opacity-50 transition-all"
                >
                  <svg className="h-4 w-4" viewBox="0 0 23 23" aria-hidden="true">
                    <rect width="10" height="10" x="1" y="1" fill="#F25022" />
                    <rect width="10" height="10" x="12" y="1" fill="#7FBA00" />
                    <rect width="10" height="10" x="1" y="12" fill="#00A4EF" />
                    <rect width="10" height="10" x="12" y="12" fill="#FFB900" />
                  </svg>
                  {loading ? "Redirecting…" : "Continue with Microsoft"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setStep("login");
                    setErr("");
                  }}
                  className="w-full text-center text-[12px] text-slate-500 hover:text-slate-300 mt-1"
                >
                  ← Back to email + password
                </button>
              </form>
            )}

            {step === "mfa" && (
              <form onSubmit={onVerifyMfa} className="mt-6 space-y-4">
                <div className="rounded-lg border border-slate-700/50 bg-[#05091a] px-3 py-2 text-[13px] text-slate-400">
                  Check your email and enter the <b className="text-slate-200">6-digit code</b>.
                </div>

                <div>
                  <label className="mb-1.5 block text-[12px] uppercase tracking-wider text-slate-400">
                    MFA code
                  </label>
                  <input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    placeholder="XYZ6KT"
                    className="w-full rounded-lg border border-slate-700/50 bg-[#05091a] px-3 py-2.5 text-[14px] text-slate-100 placeholder:text-slate-600 tracking-widest outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                    maxLength={8}
                    required
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-[12px] text-slate-600" />
                  <button
                    type="button"
                    onClick={onResendMfa}
                    disabled={resendLoading || resendLeft > 0}
                    className="text-[12px] text-indigo-300 hover:text-indigo-200 disabled:opacity-50"
                  >
                    {resendLoading
                      ? "Resending…"
                      : resendLeft > 0
                      ? `Resend in ${resendLeft}s`
                      : "Resend code"}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2.5 text-[14px] font-semibold text-white hover:from-blue-500 hover:to-indigo-500 shadow-lg shadow-indigo-900/40 disabled:opacity-50 transition-all"
                >
                  {loading ? "Verifying…" : "Verify & Continue"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setStep("login");
                    setPassword("");
                    setMfaCode("");
                    setErr("");
                    setNotice("");
                  }}
                  className="w-full text-center text-[12px] text-slate-500 hover:text-slate-300 mt-1"
                >
                  Not you? Sign in with a different account
                </button>
              </form>
            )}
          </div>

          <div className="rounded-b-2xl border-t border-indigo-500/15 bg-[#05091a]/60 px-7 py-3 text-[11.5px] text-slate-500">
            By signing in you agree to our{" "}
            <a href="https://whiteintel.io/terms" target="_blank" rel="noreferrer" className="text-indigo-300 hover:text-indigo-200">Terms</a>
            {" "}and{" "}
            <a href="https://whiteintel.io/privacy" target="_blank" rel="noreferrer" className="text-indigo-300 hover:text-indigo-200">Privacy Policy</a>.
          </div>
        </div>
    </AuthShell>
  );
}
